import { onAuthStateChanged, reload, type User } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import {
  hostProfileImageUrlFromUserData,
  resolveAccountPhotoUrl,
} from "@/utils/user/resolveAccountPhotoUrl";
import { auth } from "./auth-config";
import { db } from "./config";

const AUTH_USER_REFRESH_EVENT = "wn:auth-user-refresh";

const emitAuthUserRefresh = () => {
  if (typeof window === "undefined") {
    return;
  }

  window.dispatchEvent(new Event(AUTH_USER_REFRESH_EVENT));
};

const subscribeToAuthRefreshEvents = (
  callback: (user: User | null) => void,
) => {
  if (typeof window === "undefined") {
    return () => {};
  }

  const handleRefresh = () => {
    callback(auth?.currentUser ?? null);
  };

  window.addEventListener(AUTH_USER_REFRESH_EVENT, handleRefresh);
  return () => {
    window.removeEventListener(AUTH_USER_REFRESH_EVENT, handleRefresh);
  };
};

const cloneAuthUser = (user: User): User =>
  Object.assign(Object.create(Object.getPrototypeOf(user)), user);

const applyProfileOverrides = (
  user: User,
  data?: Record<string, unknown> | null,
): User => {
  const nextUser = cloneAuthUser(user) as User & {
    displayName: string | null;
    photoURL: string | null;
  };

  if (data && typeof data.displayName === "string") {
    nextUser.displayName = data.displayName.trim() || null;
  }

  const firestorePhotoURL =
    data && typeof data.photoURL === "string"
      ? data.photoURL
      : data && data.photoURL === null
        ? ""
        : user.photoURL;
  nextUser.photoURL = resolveAccountPhotoUrl({
    hostProfileImageUrl: hostProfileImageUrlFromUserData(data),
    firestorePhotoURL,
    authPhotoURL: user.photoURL,
  });

  return nextUser;
};

export const getCurrentAuthUser = (): User | null => auth?.currentUser ?? null;

export const refreshCurrentAuthUser = async (): Promise<User | null> => {
  const currentUser = auth?.currentUser ?? null;

  if (!currentUser) {
    emitAuthUserRefresh();
    return null;
  }

  await reload(currentUser);
  emitAuthUserRefresh();

  return auth?.currentUser ?? currentUser;
};

export const subscribeToAuthState = (callback: (user: User | null) => void) => {
  if (!auth) {
    callback(null);
    return () => {};
  }

  let unsubscribeUserDoc: (() => void) | null = null;

  const publishUser = (user: User | null, data?: Record<string, unknown> | null) => {
    if (!user) {
      callback(null);
      return;
    }

    callback(applyProfileOverrides(user, data));
  };

  const syncUserDocSubscription = (user: User | null) => {
    unsubscribeUserDoc?.();
    unsubscribeUserDoc = null;

    if (!user || !db) {
      publishUser(user);
      return;
    }

    publishUser(user);

    const subscribedUid = user.uid;
    unsubscribeUserDoc = onSnapshot(
      doc(db, "users", subscribedUid),
      (snapshot) => {
        // Ignore stale snapshots after sign-out (or account switch): the listener
        // can still fire once after auth has already cleared.
        if (auth?.currentUser?.uid !== subscribedUid) {
          return;
        }
        publishUser(
          user,
          snapshot.exists() ? (snapshot.data() as Record<string, unknown>) : null,
        );
      },
      () => {
        if (auth?.currentUser?.uid !== subscribedUid) {
          publishUser(auth?.currentUser ?? null);
          return;
        }
        publishUser(user);
      },
    );
  };

  const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
    syncUserDocSubscription(user);
  });
  const unsubscribeRefresh = subscribeToAuthRefreshEvents(() => {
    syncUserDocSubscription(auth.currentUser ?? null);
  });

  return () => {
    unsubscribeUserDoc?.();
    unsubscribeAuth();
    unsubscribeRefresh();
  };
};
